import type { SessionMode } from "@mathbee/engine";
// subpath import: the full engine loads only with the play screens
import { ENGINE_API_VERSION } from "@mathbee/engine/version";
import { api } from "../api/client.ts";
import type { ChildState, SyncResult } from "../api/types.ts";

/**
 * Finished sessions wait here until the server confirms them (SPEC §10.5, M1: localStorage;
 * T4.6 moves it to IndexedDB). The server is idempotent per session id, so retries are safe.
 */
export interface OutboxSession {
  id: string;
  lessonId: string | null;
  mode: SessionMode | "game" | "speed";
  /** island of a boss session */
  topicId?: string;
  /** engine contract of the app that played it (a session may sync after an app update) */
  engineVersion?: number;
  /** notebook: input level 1 Chọn, 2 Xếp, 3 Viết (SPEC 5.6) */
  notebookLevel?: 1 | 2 | 3;
  /** failed attempts to sync it; kept in the archive instead of being dropped after 5 */
  rejections?: number;
  sessionIndex: number;
  seed: string;
  startedAt: string;
  finishedAt: string;
  attempts: {
    id: string;
    lessonId?: string;
    itemId: string;
    variant: { templateId: string; seed: string } | null;
    answers: unknown[];
    solutionShown: boolean;
    hintsUsed: number;
    durationMs: number;
    contentHash?: string;
    client?: { tries: number; solutionShown: boolean; score: number };
  }[];
  /** board mini-game: the server replays it (SPEC §7.9) */
  game?: {
    gameId: string;
    book: boolean;
    answers: (number | null)[];
    /** "2 bé cùng máy": the second child of the household, paid on their own session */
    friend?: { childId: string; sessionId: string; answers: (number | null)[] };
  };
  /** "Đấu nhanh": the server regenerates the questions from seed + skills (M2b.2a) */
  speed?: { skills: string[]; answers: (number | null)[] };
}

const KEY = "mb-outbox-v1";
const ARCHIVE_KEY = "mb-outbox-archive-v1";
const MAX_REJECTIONS = 5;

/** Sessions the server refused 5 times: kept for support, never silently deleted. */
function archive(childId: string, sessions: OutboxSession[]) {
  try {
    const all = JSON.parse(localStorage.getItem(ARCHIVE_KEY) ?? "{}") as Record<
      string,
      OutboxSession[]
    >;
    all[childId] = [...(all[childId] ?? []), ...sessions];
    localStorage.setItem(ARCHIVE_KEY, JSON.stringify(all));
  } catch {
    /* storage blocked */
  }
}

function read(): Record<string, OutboxSession[]> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, OutboxSession[]>;
  } catch {
    return {};
  }
}
function write(data: Record<string, OutboxSession[]>) {
  try {
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* storage full or blocked: the session is lost only if the network also fails */
  }
}

export function enqueue(childId: string, session: OutboxSession) {
  const all = read();
  all[childId] = [...(all[childId] ?? []), session];
  write(all);
}

export function pendingCount(childId: string): number {
  return read()[childId]?.length ?? 0;
}

/** Send everything pending for the active child. Returns results + fresh state, or null when offline. */
export async function flush(
  childId: string,
): Promise<{ results: SyncResult[]; state: ChildState } | null> {
  const pending = read()[childId] ?? [];
  if (pending.length === 0) return null;
  try {
    const res = await api.post<{ results: SyncResult[]; state: ChildState }>("/sync", {
      engineVersion: ENGINE_API_VERSION,
      sessions: pending.slice(0, 20),
    });
    // only a session the server stored (or already had) leaves the queue: a rejected one is
    // retried, and after MAX_REJECTIONS moves to an archive that is never deleted (T8.6)
    const stored = new Set(
      res.results.filter((r) => r.status !== "rejected").map((r) => r.sessionId),
    );
    const rejected = new Set(
      res.results.filter((r) => r.status === "rejected").map((r) => r.sessionId),
    );
    const all = read();
    const keep: OutboxSession[] = [];
    const dead: OutboxSession[] = [];
    for (const s of all[childId] ?? []) {
      if (stored.has(s.id)) continue;
      const tries = (s.rejections ?? 0) + (rejected.has(s.id) ? 1 : 0);
      (tries >= MAX_REJECTIONS ? dead : keep).push({ ...s, rejections: tries });
    }
    all[childId] = keep;
    write(all);
    if (dead.length) archive(childId, dead);
    return res;
  } catch {
    return null;
  }
}
